import { ENHANCED_FNRI_COLORS, FNRI_COLORS } from "../constants/maps-constants";
import { CategoryConversion } from "./top-section-utils";

export const ENHANCED_NRI_SCALE: CategoryConversion[] = [
  {
    label: "No Exposure" as const,
    color: ENHANCED_FNRI_COLORS.NO_EXPOSURE,
    lossRateRange: "Zero loss expected",
    matches: (score: number) => score === 0,
  },
  {
    label: "Negligible" as const,
    color: ENHANCED_FNRI_COLORS.NEGLIGIBLE,
    lossRateRange: "< 0.01% of building value/yr",
    matches: (score: number) => score > 0 && score < 0.01,
  },
  {
    label: "Low" as const,
    color: ENHANCED_FNRI_COLORS.LOW,
    lossRateRange: "0.01% - 5% of building value/yr",
    matches: (score: number) => score >= 0.01 && score < 5,
  },
  {
    label: "Moderate" as const,
    color: ENHANCED_FNRI_COLORS.MODERATE,
    lossRateRange: "5% - 20% of building value/yr",
    matches: (score: number) => score >= 5 && score < 20,
  },
  {
    label: "Elevated" as const,
    color: ENHANCED_FNRI_COLORS.ELEVATED,
    lossRateRange: "20% - 50% of building value/yr",
    matches: (score: number) => score >= 20 && score < 50,
  },
  {
    label: "High" as const,
    color: ENHANCED_FNRI_COLORS.HIGH,
    lossRateRange: "50% - 80% of building value/yr",
    matches: (score: number) => score >= 50 && score < 80,
  },
  {
    label: "Very High" as const,
    color: ENHANCED_FNRI_COLORS.VERY_HIGH,
    lossRateRange: "80% - <100% of building value/yr",
    matches: (score: number) => score >= 80 && score < 100,
  },
  {
    label: "Maximum" as const,
    color: ENHANCED_FNRI_COLORS.MAXIMUM,
    lossRateRange: ">= 100% of building value/yr",
    matches: (score: number) => score >= 100,
  },
];

export const NORMAL_NRI_SCALE = [
  {
    label: "No Exposure",
    lossRateRange: "Zero loss expected",
    color: "transparent",
    matches: (score: number) => score === 0,
  },
  {
    label: "Very Low",
    lossRateRange: "Very low expected annual loss",
    color: FNRI_COLORS.VERY_LOW,
    matches: (score: number) => score > 0 && score <= 20,
  },
  {
    label: "Low",
    lossRateRange: "Low expected annual loss",
    color: FNRI_COLORS.LOW,
    matches: (score: number) => score > 20 && score <= 40,
  },
  {
    label: "Moderate",
    lossRateRange: "Moderate expected annual loss",
    color: FNRI_COLORS.MEDIUM,
    matches: (score: number) => score > 40 && score <= 60,
  },
  {
    label: "High",
    lossRateRange: "High expected annual loss",
    color: FNRI_COLORS.HIGH,
    matches: (score: number) => score > 60 && score <= 80,
  },
  {
    label: "Very High",
    lossRateRange: "Very high expected annual loss",
    color: FNRI_COLORS.VERY_HIGH,
    matches: (score: number) => score > 80 && score <= 100,
  },
];

const RISK_LABEL_ALIASES: Record<string, string> = {
  medium: "Moderate",
  extreme: "Maximum",
};

export const getNriScaleEntryForLabel = (
  label: unknown,
): { conversion: CategoryConversion } | null => {
  if (typeof label !== "string" || !label.trim()) return null;
  const normalized = label.trim().toLowerCase();
  const target = (RISK_LABEL_ALIASES[normalized] ?? label.trim()).toLowerCase();
  const index = ENHANCED_NRI_SCALE.findIndex(
    (conversion) => conversion.label.toLowerCase() === target,
  );
  if (index < 0) return null;
  return { conversion: ENHANCED_NRI_SCALE[index] };
};
